/**
 * Document-level triggers configuration for pharmacy order update rule configs.
 * Supports cron, api, order_start, and before_stp trigger types.
 */

import { Button, Select, TextInput } from "risa-oasis-ui_v2";
import type { CallingRuleTrigger } from "../../../pages/configurations/pages/callingConfiguration/callingRuleUtils";
import { AddIcon } from "../../../svg/add-icon";
import { Delete } from "../../../svg/delete";
import { getTriggerTypeOptions } from "../shared/ruleFormConstants";

interface CallingRuleTriggersSectionProps {
  triggers: CallingRuleTrigger[];
  /** Bumped when Firestore data is applied so Select controls remount with saved values. */
  loadedVersion?: number;
  onAddTrigger: () => void;
  onRemoveTrigger: (index: number) => void;
  onTypeChange: (index: number, value: string) => void;
  onScheduleChange: (index: number, schedule: string) => void;
}

/** Renders the document-level triggers card (type only; cron requires schedule). */
const CallingRuleTriggersSection = ({
  triggers,
  loadedVersion = 0,
  onAddTrigger,
  onRemoveTrigger,
  onTypeChange,
  onScheduleChange,
}: CallingRuleTriggersSectionProps) => (
  <div className="flex w-full flex-col gap-3 rounded-md border border-primaryGray-14 p-4">
    <div className="flex flex-row items-center justify-between">
      <div className="text-sm font-semibold">Triggers</div>
      <Button
        buttonType="tertiary"
        size="small"
        disabled={false}
        onClick={onAddTrigger}
      >
        <AddIcon width="14" height="14" color="#0056d6" />
        Add trigger
      </Button>
    </div>
    {triggers.length === 0 ? (
      <div className="text-tiny text-primaryGray-12">
        No triggers configured.
      </div>
    ) : (
      <div className="grid w-full gap-3 lg:grid-cols-2">
        {triggers.map((trigger, index) => {
          const isCron = trigger.type === "cron";
          const schedule = (trigger as { schedule?: string }).schedule ?? "";
          return (
            <div
              key={`trigger-${index}-${trigger.type}-${loadedVersion}`}
              className="flex flex-row items-end gap-2 rounded-md border border-primaryGray-16 bg-primaryGray-16/40 p-3"
            >
              <div className="w-40 shrink-0">
                <Select
                  key={`trigger-type-${index}-${trigger.type}-${loadedVersion}`}
                  id={`trigger-type-${index}`}
                  label=""
                  options={getTriggerTypeOptions(trigger.type)}
                  placeholder="Select type"
                  defaultValue={trigger.type}
                  value={trigger.type}
                  onOptionChange={(data) => onTypeChange(index, data.value)}
                />
              </div>
              {isCron ? (
                <div className="mb-1.5 min-w-0 flex-1">
                  <TextInput
                    key={`trigger-schedule-${index}-${schedule}-${loadedVersion}`}
                    id={`trigger-schedule-${index}`}
                    label=""
                    placeholder="0 12 * * *"
                    defaultValue={schedule}
                    onChange={(d) => onScheduleChange(index, d.value ?? "")}
                    onBlur={(d) => onScheduleChange(index, d.value ?? "")}
                  />
                </div>
              ) : (
                <div className="flex-1" />
              )}
              <div
                className="mb-5 shrink-0 cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label="Remove trigger"
                onClick={() => onRemoveTrigger(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRemoveTrigger(index);
                  }
                }}
              >
                <Delete width={16} height={16} />
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

export default CallingRuleTriggersSection;
